import type { ReactNode, RefObject } from 'react';
import { forwardRef, Fragment, memo, useState } from 'react';
import { Picture, Button } from '@ui';
import { oysterBarQuestionImage } from '@images';
import CallbackModal from '@components/Modals/CallbackModal';
import SuccessModalWithTimer from '@components/Modals/SuccessModalWithTimer';
import clsx from 'clsx';
import icons from '@icons';
import './styles.scss';
import { twcls } from '@utils/twcls';

const {
  arrowIosLeft: { black: arrowBlackOutlined }
} = icons.Outline;

const faq = (
  isCatering?: boolean
): Array<{
  id: number;
  question: string;
  answer: ReactNode;
}> =>
  isCatering
    ? [
        {
          id: 0,
          question: 'Для каких мероприятий подойдет бар?',
          answer: (
            <p>
              Наш бар идеально впишется в корпоратив, на свадьбу, частную
              вечеринку, день рождения, другое торжество. Программа станет
              изысканным аперитивом или главным гастрономическим событием
              вечера.
            </p>
          )
        },
        {
          id: 1,
          question: 'Насколько заранее нужно сделать заказ?',
          answer: (
            <p>
              Заявки принимаются не менее, чем за 5 рабочих дней до начала
              мероприятия. Это необходимо для того, чтобы мы успели согласовать
              организационные моменты, гарантировать наличие требуемого
              количества свежих морепродуктов.
            </p>
          )
        },
        {
          id: 2,
          question: 'Что входит в стоимость?',
          answer: (
            <p>
              Итоговая цена формируется из нескольких составляющих: базового
              обслуживания (работа экайе, доставка, оформление), стоимости
              выбранных морепродуктов, необходимости оказания опциональных услуг
              (дополнительный персонал, мастер-класс и т.д.).
            </p>
          )
        },
        {
          id: 3,
          question: 'Вы работаете с индивидуальными пожеланиями по меню?',
          answer: (
            <p>
              Да, мы всегда открыты к обсуждению индивидуальных предпочтений
              клиентов. Менеджер скорректирует меню под конкретные запросы и
              бюджет.
            </p>
          )
        },
        {
          id: 4,
          question: 'Сколько времени нужно на подготовку?',
          answer: (
            <p>
              Подготовка и установка бара на месте мероприятия занимает около
              одного часа. Вы и ваши гости увидите результат максимально быстро.
              <br />
              Позвоните по телефону{' '}
              <a
                href='tel:+74956409640'
                className='text-[var(--dark-red)] transition-colors hover:text-[var(--main-red)]'
              >
                +7 (495) 640-9-640
              </a>{' '}
              или оставьте заявку на обратный звонок на сайте, чтобы обсудить
              детали праздника, сделать его по-настоящему тематическим морским и
              незабываемым!
            </p>
          )
        }
      ]
    : [
        {
          id: 0,
          question: 'Для каких мероприятий подойдет бар?',
          answer: (
            <p>
              Наш бар идеально впишется в корпоратив, на свадьбу, частную
              вечеринку, день рождения, другое торжество. Программа станет
              изысканным аперитивом или главным гастрономическим событием
              вечера.
            </p>
          )
        },
        {
          id: 1,
          question: 'Насколько заранее нужно сделать заказ?',
          answer: (
            <p>
              Заявки принимаются не менее, чем за 5 рабочих дней до начала
              мероприятия. Это необходимо для того, чтобы мы успели согласовать
              организационные моменты, гарантировать наличие требуемого
              количества свежих морепродуктов.
            </p>
          )
        },
        {
          id: 2,
          question: 'Что входит в стоимость?',
          answer: (
            <p>
              Итоговая цена формируется из нескольких составляющих: базового
              обслуживания (работа экайе, доставка, оформление), стоимости
              выбранных морепродуктов, необходимости оказания опциональных услуг
              (дополнительный персонал, мастер-класс и т.д.).
            </p>
          )
        },
        {
          id: 3,
          question: 'Вы работаете с индивидуальными пожеланиями по меню?',
          answer: (
            <p>
              Да, мы всегда открыты к обсуждению индивидуальных предпочтений
              клиентов. Менеджер&nbsp;скорректирует меню под конкретные запросы
              и бюджет.{' '}
            </p>
          )
        }
      ];
interface RemainingQuestionsProps {
  yellow?: boolean;
  noMb?: boolean;
  withFAQ?: boolean;
  titleClassName?: string;
  isCatering?: boolean;
  setFaqRef?: (ref: { current: HTMLDivElement | null }) => void;
  faqRef?: RefObject<HTMLDivElement | null>;
}

const RemainingQuestions = forwardRef<HTMLDivElement, RemainingQuestionsProps>(
  (
    {
      noMb,
      titleClassName,
      withFAQ = false,
      yellow = false,
      isCatering,
      faqRef
    },
    ref
  ) => {
    const [showCallbackModal, setShowCallbackModal] = useState(false);
    const [successModalText, setSuccessModalText] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const closeCallbackModal = (): void => {
      setShowCallbackModal(false);
    };

    const openCallbackModal = (): void => {
      setShowCallbackModal(true);
    };

    const closeSuccessModal = (): void => {
      setShowSuccessModal(false);
    };

    const changeToSuccessModal = (): void => {
      setShowCallbackModal(false);
      setShowSuccessModal(true);
      setSuccessModalText('Мы перезвоним вам в самое ближайшее время');
    };

    const faqList = faq(isCatering);

    return (
      <>
        <section
          id='contacts'
          ref={ref}
          className={clsx(
            'remaining-questions',
            noMb && 'remaining-questions--no-mb',
            yellow && 'remaining-questions--yellow'
          )}
        >
          <div className='pb-20 wrapper-container sm:pb-12 md:pb-10'>
            <div className='remaining-questions__content'>
              <h2
                className={twcls(
                  'remaining-questions__title oysterbar-test-title-secondary',
                  titleClassName
                )}
              >
                <span className='remaining-questions__title-desktop'>
                  <span className='remaining-questions__title-line'>
                    Остались{' '}
                  </span>
                  <span className='remaining-questions__title-line'>
                    вопросы?
                  </span>
                </span>
                <span className='remaining-questions__title-mobile'>
                  <span className='remaining-questions__title-line'>
                    Остались вопросы?
                  </span>
                </span>
              </h2>

              <div className='remaining-questions__contact'>
                <p className='remaining-questions__contact-text'>
                  Звоните{' '}
                  <a
                    href='tel:+74956409640'
                    className='remaining-questions__phone'
                  >
                    +7 495 640 9 640
                  </a>{' '}
                  с 06:00 до 00:00
                </p>
                <p className='remaining-questions__contact-text'>
                  или закажите обратный звонок
                </p>
              </div>

              <div className='remaining-questions__button'>
                <Button
                  type='btn'
                  size='large'
                  colour='red'
                  rank='primary'
                  text='Обратный звонок'
                  onClick={openCallbackModal}
                />
              </div>
            </div>

            <div className='remaining-questions__image'>
              <Picture icon={oysterBarQuestionImage} />
            </div>
            {withFAQ && (
              <div className='mx-auto mt-15' ref={faqRef}>
                <hr className='w-full h-px bg-[var(--mid-grey)] mb-10' />
                <div className='mb-10'>
                  <h2 className='font-medium text-4.5xl mb-15 allMd:text-xl allMd:mb-6'>
                    Ответы на частые вопросы
                  </h2>
                  <ul className=''>
                    {faqList.map((item, index) => (
                      <Fragment key={item.id}>
                        <li key={item.id} className='leading-tight '>
                          <details className='group open:mb-6'>
                            <summary
                              className={
                                ' cursor-pointer marker:hidden marker:text-[0px] flex w-full justify-between items-center mb-5 allMd:mb-3'
                              }
                            >
                              <h3 className='text-2xl font-medium sm:text-sm md:text-base'>
                                {item.question}
                              </h3>
                              <Picture
                                icon={arrowBlackOutlined}
                                width={32}
                                height={32}
                                pictureClassName='transition-transform -rotate-90 group-open:rotate-90 allMd:[&>img]:w-5 allMd:[&>img]:h-5'
                              />
                            </summary>
                            <div className='text-base allMd:text-sm max-w-[800px]'>
                              {item.answer}
                            </div>
                          </details>
                        </li>
                        {index !== faqList.length - 1 && (
                          <hr className='my-6 sm:my-4 w-full h-px bg-[var(--mid-grey)]' />
                        )}
                      </Fragment>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          <div className='wrapper-container'>
            <div className='remaining-questions__footer'>
              <div className='remaining-questions__footer-content'>
                <div className='remaining-questions__copyright'>
                  © 2005-{new Date().getFullYear()} Деликатеска.ру —
                  интернет-магазин продуктов. Все права защищены
                </div>
                <div className='remaining-questions__links'>
                  <a
                    href='/license-agreement'
                    className='remaining-questions__link'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Пользовательское соглашение
                  </a>
                  <a
                    href='/confidential'
                    className='remaining-questions__link'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Политика конфиденциальности
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {showCallbackModal && (
          <CallbackModal
            onClose={closeCallbackModal}
            changeToSuccessModal={changeToSuccessModal}
          />
        )}

        {showSuccessModal && (
          <SuccessModalWithTimer
            closeModal={closeSuccessModal}
            title='Внимание!'
            text={successModalText}
          />
        )}
      </>
    );
  }
);

export default memo(RemainingQuestions);
